import { CreateUpstreamRemoteSync, UpstreamRemote, UpstreamRemoteSync } from "@istari/istari-client";
import { queryOptions } from "@tanstack/react-query";

import { DEFAULT_CACHE_TIME } from "~/constants";

import clientSDK from "./clientSDK";

// Use UpstreamRemote directly from the API client - no custom interface needed

// Sync Status types from API design

export interface ListRemotesOptions {
  page?: number;
  size?: number;
  sort?: string;
}

export interface PagedRemotes {
  items: UpstreamRemote[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// Query keys following API design structure
export const remotesQueryKeys = {
  all: ["remotes"] as const,
  list: () => [...remotesQueryKeys.all, "list"] as const,
  detail: (id: string) => [...remotesQueryKeys.all, "detail", id] as const,
  sync: (id: string) => [...remotesQueryKeys.detail(id), "sync"] as const,
  syncStatus: (syncId: string) => ["sync-status", syncId] as const,
};

// Phase 1: Remotes Query Options

/**
 * GET api/v2/remotes
 *
 * Retrieve a list of all remotes.
 *
 * @returns Promise<UpstreamRemote[]> - Returns a list of remotes
 */
export const listRemotesOptions = () =>
  queryOptions({
    queryKey: remotesQueryKeys.list(),
    queryFn: async (): Promise<UpstreamRemote[]> => {
      const response = await clientSDK.listUpstreamRemotes();
      return response.items;
    },
    staleTime: DEFAULT_CACHE_TIME,
  });

/**
 * GET api/v2/remotes/{remote_id}
 *
 * Retrieve details of a specific remote by its ID.
 *
 * @param id - The ID of the remote to retrieve
 * @returns Promise<UpstreamRemote> - Returns the details of the specified remote
 */
export const getRemoteOptions = (id: string) =>
  queryOptions({
    queryKey: remotesQueryKeys.detail(id),
    queryFn: async (): Promise<UpstreamRemote> => {
      // Since there's no getUpstreamRemote endpoint, we list all and find the one we need
      const response = await clientSDK.listUpstreamRemotes();
      const upstreamRemote = response.items.find((remote) => remote.id === id);

      if (!upstreamRemote) {
        throw new Error(`Remote with id ${id} not found`);
      }

      return upstreamRemote;
    },
    enabled: !!id,
    staleTime: DEFAULT_CACHE_TIME,
  });

// Sync Management Query Options (Phase 1)

/**
 * GET api/v2/remotes/sync/{sync_id}
 *
 * Retrieve the status details by sync ID.
 *
 * @param syncId - The ID of the SyncRemoteResource object
 * @returns Promise<UpstreamRemoteSync> - Returns the details of the specified sync including:
 *   - id: ID of the status object
 *   - status: Status of the sync process (pending, in_progress, completed, failed, cancel)
 *   - message: Detailed message for the status (if error process failed)
 *   - created: Timestamp of when the sync was triggered
 *   - createdById: User id of who triggered the sync
 *   - updated: Timestamp of when the last update was made
 *   - upstreamRemoteId: ID of the remote the sync is being performed on
 */
export const getSyncStatusOptions = (syncId: string) =>
  queryOptions({
    queryKey: remotesQueryKeys.syncStatus(syncId),
    queryFn: async (): Promise<UpstreamRemoteSync> => {
      return await clientSDK.getUpstreamRemoteSync(syncId);
    },
    enabled: !!syncId,
    staleTime: 10 * 1000, // Refresh frequently for sync status
  });

/**
 * GET api/v2/remotes/{remote_id}/sync
 *
 * Retrieve the sync status for a specific remote by its ID.
 *
 * @param remoteId - The ID of the remote
 * Note: This endpoint doesn't exist - removing reference
 *   - items: List of the status for the remote
 *     - id: ID of the status object
 *     - status: Status of the sync process (IN_PROGRESS, COMPLETED, FAILED, CANCEL)
 *     - message: Detailed message for the status (if error process failed)
 *     - created: Timestamp of when the sync was triggered
 *     - created_by_id: User id of who triggered the sync
 *     - updated: Timestamp of when the last update was made
 *     - remote_id: ID of the remote the sync is being performed on
 *   - total: Number of all status for the remote
 *   - page: Number of the current page
 *   - size: Number of items returned
 *   - pages: Number of pages
 */
// Note: This function was removed because there's no corresponding real API endpoint
// If sync history listing is needed in the future, it should be implemented when the API is available

// Access Management Query Options (Phase 1)

/**
 * GET api/v2/outbound-remotes/{remote_id}/access
 *
 * Retrieve access details for a specific outbound remote.
 *
 * @param remoteId - The ID of the outbound remote
 * @returns Promise<PagedRemoteAccess> - Returns access details for the specified outbound remote including:
 *   - items: List of the relations for the remote
 */
// Note: Outbound remote access management functions were removed
// because there are no corresponding real API endpoints available yet.
// These should be implemented when the APIs become available:
// - getOutboundRemoteAccessOptions
// - createOutboundRemoteAccess
// - updateOutboundRemoteAccess
// - deleteOutboundRemoteAccess

// Mutation Functions

/**
 * POST api/v2/remotes/{remote_id}/sync
 *
 * Trigger a sync operation for a specific remote.
 *
 * @param remoteId - The ID of the remote to sync
 * @returns Promise<UpstreamRemoteSync> - Returns the initial sync status
 */
export const triggerRemoteSync = async (remoteId: string): Promise<UpstreamRemoteSync> => {
  return await clientSDK.syncUpstreamRemotes(new CreateUpstreamRemoteSync(remoteId));
};

// Note: updateSyncStatus function was removed because:
// 1. It's not used anywhere in the codebase
// 2. There's no corresponding real API endpoint
// If sync cancellation is needed, it should be implemented when the API is available
